import { Component, Input, OnInit } from '@angular/core';
import { ColorService } from 'src/app/service/color/color.service';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

interface CbType {
  key: string;
  title: string;
  group: string;
  note: string;
  // Index into the generateColorBlindnessColors() output array.
  index: number;
}

// Live color vision deficiency preview embedded in a blog post. Pick a color and
// see it beside how each kind of color blindness perceives it (the two-box chip:
// the real color next to the simulated one), grouped by the affected cone. Reuses
// the color tool's ColorService.generateColorBlindnessColors (color-blind lib), so
// the simulation matches /tools/color. The post marks a mount point with
// <!--CVD-START-->...<!--CVD-END-->.
@Component( {
  selector: 'app-color-blindness',
  templateUrl: './color-blindness.component.html',
  styleUrls: [ './color-blindness.component.scss' ],
} )
export class ColorBlindnessComponent implements OnInit {

  @Input() heading = 'Try It Yourself';

  // A leaf green to start: red and green are exactly what the common (and most
  // prevalent) red-green deficiencies confuse, so the simulated swatches shift hard and
  // the reader sees the point immediately. Greens drift toward tan / khaki under protan
  // and deutan, which is the whole reason the widget exists.
  hex = '#2FA84F';
  text = '';
  copiedKey = '';

  private sim: { header: string; value: string }[] = [];
  private tracked = false;

  // The eight simulations, ordered so each cone family reads mild (anomaly) then
  // severe (anopia). `index` maps each type to its simulated swatch.
  readonly types: CbType[] = [
    { key: 'protanomaly', title: 'Protanomaly', group: 'Protan (red)', note: 'Shifted red-cone sensitivity. Reds desaturate and darken.', index: 0 },
    { key: 'protanopia', title: 'Protanopia', group: 'Protan (red)', note: 'No working red cones. Red and green are confused and reds look dim.', index: 4 },
    { key: 'deuteranomaly', title: 'Deuteranomaly', group: 'Deutan (green)', note: 'Shifted green-cone sensitivity. The most common deficiency of all.', index: 1 },
    { key: 'deuteranopia', title: 'Deuteranopia', group: 'Deutan (green)', note: 'No working green cones. Red and green are confused at normal brightness.', index: 5 },
    { key: 'tritanomaly', title: 'Tritanomaly', group: 'Tritan (blue)', note: 'Shifted blue-cone sensitivity. Blue and green start to converge.', index: 2 },
    { key: 'tritanopia', title: 'Tritanopia', group: 'Tritan (blue)', note: 'No working blue cones. Blue and yellow are confused.', index: 6 },
    { key: 'achromatomaly', title: 'Achromatomaly', group: 'Monochromacy (no color)', note: 'All three cone types weakened, leaving a heavily desaturated image.', index: 3 },
    { key: 'achromatopsia', title: 'Achromatopsia', group: 'Monochromacy (no color)', note: 'No working cones. Vision reduces to brightness alone, seen as grayscale.', index: 7 },
  ];

  constructor(
    private colorService: ColorService,
    private analyticsService: AnalyticsService,
  ) {
  }

  ngOnInit(): void {
    // Open on the fixed green above (not a random color) so the simulation always shows
    // an obvious shift, the point the reader is here to see.
    this.text = this.hex;
    this.recompute();
  }

  // The same types grouped by clinical category, one sub-heading per group.
  get groups(): { group: string; types: CbType[] }[] {
    return this.types.reduce( ( groups: { group: string; types: CbType[] }[], t ) => {
      const last = groups[ groups.length - 1 ];
      if ( last && last.group === t.group ) {
        last.types.push( t );
      } else {
        groups.push( { group: t.group, types: [ t ] } );
      }
      return groups;
    }, [] );
  }

  get swatch(): string {
    return this.hex;
  }

  simFor( t: CbType ): string {
    return ( this.sim[ t.index ] && this.sim[ t.index ].value ) || this.hex;
  }

  onText( value: string ): void {
    const raw = ( value || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    this.text = '#' + raw;
    this.recompute();
    this.track();
  }

  onPicker( value: string ): void {
    this.text = ( value || '' ).toLowerCase();
    this.recompute();
    this.track();
  }

  onKey( event: KeyboardEvent ): void {
    if ( event.ctrlKey || event.metaKey ) {
      return;
    }
    if ( event.key && event.key.length === 1 && !/[0-9a-fA-F]/.test( event.key ) ) {
      event.preventDefault();
    }
  }

  copy( hex: string, key: string ): void {
    if ( navigator?.clipboard ) {
      navigator.clipboard.writeText( hex ).catch( () => { /* ignore */ } );
    }
    this.copiedKey = key;
    this.track();
  }

  private recompute(): void {
    const v = ( this.text || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    if ( v.length !== 6 || !this.colorService.isValidHexCode( v ) ) {
      return;
    }
    this.hex = '#' + v;
    this.sim = this.colorService.generateColorBlindnessColors( v );
    this.copiedKey = '';
  }

  private track(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'color-blindness' } );
  }
}
